import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { ArrowButton } from "@/components/ArrowButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock, CalendarDays, MapPin } from "lucide-react";
import { ATENDIMENTOS } from "@/data/atendimentos";
import { waTopic } from "@/data/site";

const API = process.env.REACT_APP_BACKEND_URL;

const FACT_ICONS = [
  { key: "duracao", label: "Duração", icon: Clock },
  { key: "frequencia", label: "Frequência", icon: CalendarDays },
  { key: "modalidade", label: "Modalidade", icon: MapPin },
];

const planDescription = (p) =>
  p.sessions ? `${p.sessions} ${p.sessions === 1 ? "sessão" : "sessões"}` : "Processo completo";

const Atendimento = () => {
  const { slug } = useParams();
  const content = ATENDIMENTOS[slug];
  const [service, setService] = useState(null);
  const [plansError, setPlansError] = useState(false);

  useEffect(() => {
    setService(null);
    setPlansError(false);
    axios
      .get(`${API}/api/journey/services/${slug}`)
      .then((res) => setService(res.data))
      .catch(() => setPlansError(true));
  }, [slug]);

  if (!content && plansError) {
    return (
      <section className="container-x flex min-h-[70vh] flex-col items-start justify-center pt-28" data-testid="atendimento-not-found">
        <p className="eyebrow">Atendimento</p>
        <h1 className="mt-4 font-serif text-4xl font-light text-ink sm:text-5xl">Atendimento não encontrado</h1>
        <div className="mt-8">
          <ArrowButton to="/" testId="atendimento-not-found-home">Voltar ao início</ArrowButton>
        </div>
      </section>
    );
  }

  if (!content && !service) {
    return (
      <section className="container-x pt-28" data-testid="atendimento-loading">
        <div className="mt-10 h-72 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" />
      </section>
    );
  }

  const view = content || {
    eyebrow: service.category || "Atendimento",
    seoTitle: `${service.title} | Sabrina Mota — Psicóloga CRP 06/201923`,
    seoDescription: service.description || "Atendimento com Sabrina Mota, Psicóloga CRP 06/201923.",
    lead: service.description,
    facts: {
      duracao: service.duration,
      frequencia: service.frequency || "A combinar",
      modalidade: service.modalities.map((m) => (m === "online" ? "Online" : "Presencial")).join(" ou "),
    },
    paraQuem: null,
    comoFunciona: null,
    comoAcontece: null,
    faq: null,
    investimentoNote:
      "Os valores são compartilhados com transparência no primeiro contato. Escolher aqui pelo site não gera nenhuma cobrança nem compromisso.",
  };

  return (
    <>
      <SEO title={view.seoTitle} description={view.seoDescription} path={`/atendimento/${slug}`} />

      <section className="pt-28 lg:pt-40" data-testid="atendimento-page">
        <div className="container-x pb-16 lg:pb-20">
          <FadeUp>
            <p className="eyebrow" data-testid="atendimento-eyebrow">{view.eyebrow}</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="atendimento-title">
              {service?.title || view.seoTitle.split("|")[0].trim()}
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="lead mt-7 max-w-2xl" data-testid="atendimento-lead">{view.lead}</p>
          </FadeUp>
          <FadeUp delay={0.24} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <ArrowButton to={`/agendar/${slug}`} testId="atendimento-escolher-button">
              Escolher atendimento
            </ArrowButton>
            <ArrowButton href={waTopic(service?.title || "atendimento")} target="_blank" rel="noopener noreferrer" variant="outline" testId="atendimento-duvida-button">
              Tirar uma dúvida
            </ArrowButton>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-raiz-900/10 bg-cream py-14 lg:py-16" data-testid="atendimento-facts">
        <div className="container-x">
          <Stagger className="grid gap-6 md:grid-cols-3" gap={0.1}>
            {FACT_ICONS.map((f) => (
              <StaggerItem key={f.key}>
                <div className="h-full rounded-3xl border border-raiz-900/10 bg-paper p-7" data-testid={`atendimento-fact-${f.key}`}>
                  <f.icon aria-hidden="true" className="h-6 w-6 text-gold-600" />
                  <p className="mt-5 text-xs uppercase tracking-[0.24em] text-raiz-600">{f.label}</p>
                  <p className="mt-2 font-serif text-lg leading-snug text-ink">{view.facts[f.key]}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {view.paraQuem && (
      <section className="py-20 lg:py-28" data-testid="atendimento-para-quem">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Para quem é" title="Este atendimento pode fazer sentido se você…" testId="para-quem" />
          </div>
          <div className="lg:col-span-7">
            <Stagger className="space-y-5" gap={0.08}>
              {view.paraQuem.map((p) => (
                <StaggerItem key={p}>
                  <p className="flex items-start gap-4 text-base leading-relaxed text-ink/75" data-testid="para-quem-item">
                    <span aria-hidden="true" className="mt-3 block h-px w-8 shrink-0 bg-gold-500" />
                    {p}
                  </p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>
      )}

      {view.comoFunciona && (
      <section className="border-t border-raiz-900/10 bg-cream py-20 lg:py-28" data-testid="atendimento-como-funciona">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Como funciona" title="Um processo construído com você" testId="como-funciona" />
          </div>
          <div className="space-y-6 lg:col-span-7">
            {view.comoFunciona.map((t) => (
              <FadeUp key={t.slice(0, 24)}>
                <p className="text-base leading-relaxed text-ink/75 md:text-lg">{t}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      )}

      {view.comoAcontece && (
      <section className="py-20 lg:py-28" data-testid="atendimento-etapas">
        <div className="container-x">
          <SectionHeading eyebrow="Como acontece" title="O caminho, passo a passo" testId="etapas" />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" gap={0.1}>
            {view.comoAcontece.map((e, i) => (
              <StaggerItem key={e.title} className="h-full">
                <div className="group h-full rounded-3xl border border-raiz-900/10 bg-paper p-7 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-gold-400/60" data-testid={`etapa-${i + 1}`}>
                  <span className="font-serif text-3xl font-light text-raiz-300 transition-colors duration-500 group-hover:text-gold-500">
                    0{i + 1}
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-ink">{e.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{e.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      )}

      <section className="border-t border-raiz-900/10 bg-raiz-900 py-20 text-raiz-50 lg:py-28" data-testid="atendimento-investimento">
        <div className="container-x">
          <SectionHeading dark eyebrow="Formatos" title="Formatos de atendimento" testId="investimento" />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-3" gap={0.1}>
            {(service?.plans || []).map((p) => (
              <StaggerItem key={p.id} className="h-full">
                <div className="flex h-full flex-col rounded-3xl border border-raiz-50/15 bg-raiz-800/60 p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-gold-400/50" data-testid={`plano-${p.id}`}>
                  <p className="text-xs uppercase tracking-[0.24em] text-gold-300">{planDescription(p)}</p>
                  <h3 className="mt-4 font-serif text-2xl font-light">{p.name}</h3>
                </div>
              </StaggerItem>
            ))}
            {!service && !plansError &&
              [0, 1, 2].map((i) => (
                <div key={i} className="h-44 animate-pulse rounded-3xl border border-raiz-50/10 bg-raiz-800/40" data-testid="plano-skeleton" />
              ))}
          </Stagger>
          {plansError && (
            <p className="mt-8 text-sm text-raiz-100/70" data-testid="plano-error">
              Não foi possível carregar os formatos agora. Converse comigo pelo WhatsApp para conhecer as opções.
            </p>
          )}
          <FadeUp delay={0.15}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-raiz-100/70" data-testid="investimento-note">
              {view.investimentoNote}
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-8">
            <ArrowButton to={`/agendar/${slug}`} variant="light" testId="investimento-escolher-button">
              Escolher atendimento
            </ArrowButton>
          </FadeUp>
        </div>
      </section>

      {view.faq && (
      <section className="py-20 lg:py-28" data-testid="atendimento-faq">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Perguntas frequentes" title="Dúvidas comuns" testId="faq" />
          </div>
          <div className="lg:col-span-7">
            <FadeUp>
              <Accordion type="single" collapsible className="w-full">
                {view.faq.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg font-normal text-ink">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-ink/70 md:text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeUp>
          </div>
        </div>
      </section>
      )}

      <section className="border-t border-raiz-900/10 bg-cream py-20 text-center lg:py-24" data-testid="atendimento-cta-final">
        <div className="container-x">
          <FadeUp>
            <h2 className="mx-auto max-w-2xl font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl">
              Pronto para <em className="italic text-raiz-700">começar?</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-ink/70">
              Escolha a modalidade, o formato e o melhor horário — sem cobrança e sem compromisso nesta etapa.
            </p>
          </FadeUp>
          <FadeUp delay={0.18} className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <ArrowButton to={`/agendar/${slug}`} testId="cta-final-escolher-button">
              Escolher atendimento
            </ArrowButton>
            <ArrowButton href={waTopic("atendimento")} target="_blank" rel="noopener noreferrer" variant="outline" testId="cta-final-whatsapp-button">
              Prefiro conversar no WhatsApp
            </ArrowButton>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Atendimento;
